import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { typedQueryClient, useTypedMutation, userKey } from "hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { isAppointmentInSelectedList } from "modules/Appointments/components/NewAppointment/utils";
import { AppointmentsQueryKeys } from "modules/Appointments/queryKeys";
import { ApiInstance } from "utils/Api";

type AppointmentDatesPayload = {
  next_date?: string;
  time_car_app?: string;
  scheduled_in?: string;
  scheduled_out?: string;
};

export const useAppointmentDatesMutation = (appointment_id: number) => {
  const { t } = useTranslation();

  return useTypedMutation<AppointmentDatesPayload, void>({
    mutationFn: async payload => {
      await ApiInstance.post("/appointments/update_dates", { appointment_id, ...payload });
    },
    onSuccess: payload => {
      const appointmentDetails = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (appointmentDetails) typedQueryClient.setQueryData(AppointmentDetailsKeys.view, { ...appointmentDetails, ...payload });

      const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list);
      if (!appointments?.length) return;

      const appointmentIdx = appointments.findIndex(appointment => appointment.id === appointment_id);
      if (appointmentIdx === -1) return;

      const { time_car_app, next_date, scheduled_in, scheduled_out } = payload;
      const appointment = appointments[appointmentIdx];
      const updatedAppointment = {
        ...appointment,
        time_car_app: time_car_app ?? appointment.time_car_app,
        next_date: next_date ?? appointment.next_date,
        due_in: scheduled_in ?? appointment.due_in,
        car_return_time: scheduled_out ?? appointment.car_return_time
      };

      const selectedDate = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDateToList) ?? new Date();
      const selectedDates = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDatesToList);
      const listByScheduledRange = !!typedQueryClient.getQueryData(userKey)?.list_appointments_by_scheduled_range;
      const belongsToSelectedList = isAppointmentInSelectedList({ appointment: updatedAppointment, selectedDate, ...(selectedDates ?? {}), listByScheduledRange });

      typedQueryClient.setQueryData(
        AppointmentsQueryKeys.list,
        belongsToSelectedList ? appointments.with(appointmentIdx, updatedAppointment) : appointments.toSpliced(appointmentIdx, 1)
      );
    },
    onError: e => toast.error(e.message.startsWith("v8_") ? t(e.message) || e.message : e.message)
  });
};
